import React, { useState, useEffect, useRef } from "react";
import ReactTable from "react-table";
import { Button, UncontrolledTooltip } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport } from "@fortawesome/free-solid-svg-icons";
import AnualidadSnapshot from "../../../../types/AnualidadSnapshot";
import { formatIsoDate, formatMoney } from "../../../helpers/formatters";
import { getRelevanciaComision } from "./relevanciaComision";
import s from "./styles.scss";

/**
 * REPORTE GENERAL DE VENDEDORES — Tabla
 * Pantalla: /reportes/reporte-general-vendedor
 *
 * Columnas: Cotización, Periodo, Folio Factura, Vendedor, Descripción,
 * Membresía, Precio Venta, Relevante para comisión (getRelevanciaComision).
 * Cabeceras: negrita y centradas (styles.thHeader).
 */

export interface TableProps {
  page: Page<AnualidadSnapshot>;
  loading: boolean;
  onFetchData: ({ page, pageSize }) => void;
  onExport: () => void;
}

export const MemoTable = React.memo(
  ({ page, loading, onFetchData, onExport }: TableProps) => {
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);
    // ReactTable dispara onFetchData al montar; el submit ya cargó esos datos.
    const skipMountFetch = useRef(true);

    useEffect(() => {
      if (page) {
        setPageNumber(page.number || 0);
        setTotalPages(page.totalPages || 0);
      }
    }, [page]);

    return (
      <div>
        <div className="row">
          {!!page && !!page.content && !!page.content.length && (
            <>
              <div className="col-6 p-0" style={{ lineHeight: "40px" }}>
                <span>
                  &nbsp;&nbsp;&nbsp;&nbsp;Anualidades mostradas:{" "}
                  {page.numberOfElements} de {page.totalElements}
                </span>
              </div>
              <div
                className="col-6 p-0 text-right"
                style={{ lineHeight: "40px" }}
              >
                <Button
                  id="btnReportExportCsv"
                  color="black"
                  type="button"
                  onClick={onExport}
                >
                  Exportar <FontAwesomeIcon icon={faFileExport} color="black" />
                </Button>
              </div>
            </>
          )}
        </div>
        <ReactTable
          manual
          data={(page && page.content) || []}
          columns={columns}
          pages={totalPages}
          loading={loading}
          onFetchData={state => {
            if (skipMountFetch.current) {
              skipMountFetch.current = false;
              return;
            }
            onFetchData({ page: state.page, pageSize: state.pageSize });
          }}
          page={pageNumber}
          defaultPageSize={100}
          showPageSizeOptions={false}
          className="-striped -highlight"
          previousText="Anterior"
          nextText="Siguiente"
          loadingText="Cargando..."
          noDataText="No hay registros"
          pageText="Página"
          ofText="de"
          rowsText="filas"
        />
      </div>
    );
  }
);

const columns = [
  {
    Header: "Cotización",
    id: "idCotizacionItem",
    className: s.tdClear,
    headerClassName: s.thHeader,
    width: 80,
    accessor: simpleAccessor("idCotizacionItem")
  },
  {
    Header: "Periodo",
    id: "periodo",
    className: s.tdClear,
    headerClassName: s.thHeader,
    width: 100,
    accessor: dateAccessor("periodo")
  },
  {
    Header: "Folio Factura",
    id: "folioFactura",
    className: s.tdClear,
    headerClassName: s.thHeader,
    width: 120,
    accessor: simpleAccessor("folioFactura")
  },
  {
    Header: "Vendedor",
    id: "vendedor",
    className: s.tdClear,
    headerClassName: s.thHeader,
    width: 200,
    accessor: simpleAccessor("vendedor")
  },
  {
    Header: "Descripción",
    id: "descripcion",
    className: s.tdClear,
    headerClassName: s.thHeader,
    accessor: simpleAccessor("descripcion")
  },
  {
    Header: "Membresía",
    id: "membresiaLink",
    className: s.tdClear,
    headerClassName: s.thHeader,
    width: 150,
    accessor: (row: AnualidadSnapshot) => {
      const href = `/membresias-socios/${row.idMembresiaSocio}?idClub=${row.idClubBase}&idPublico=${row.idPublico}`;
      const label = `${row.idPublico} de ${row.clubBase}`;

      return (
        <div className={fieldClassName(row)}>
          <a href={href} target="_blank" rel="noreferrer">
            {label}
          </a>
        </div>
      );
    }
  },
  {
    Header: "Precio Venta",
    id: "precioVenta",
    className: s.tdClear,
    headerClassName: s.thHeader,
    width: 100,
    accessor: moneyAccessor("precioVenta")
  },
  {
    Header: "Relevante para comisión",
    id: "relevanteComision",
    className: s.tdClear,
    headerClassName: s.thHeader,
    width: 160,
    accessor: (row: AnualidadSnapshot) => {
      const { relevante, motivo } = getRelevanciaComision(row);
      const tipId = `rel-${row.idCotizacionItem}`;
      return (
        <div className={fieldClassName(row)}>
          <span
            id={tipId}
            className={relevante ? s.relevanteSi : s.relevanteNo}
          >
            {relevante ? "Sí" : "No"}
          </span>
          <UncontrolledTooltip placement="top" target={tipId}>
            {motivo}
          </UncontrolledTooltip>
        </div>
      );
    }
  }
];

function simpleAccessor(key: keyof AnualidadSnapshot) {
  return (row: AnualidadSnapshot) => {
    return <div className={fieldClassName(row)}>{row[key]}&nbsp;</div>;
  };
}

function dateAccessor(key: keyof AnualidadSnapshot) {
  return (row: AnualidadSnapshot) => {
    const fecha = row[key] ? formatIsoDate(row[key] as string) : "";
    return <div className={fieldClassName(row)}>{fecha}&nbsp;</div>;
  };
}

function moneyAccessor(accessor: string) {
  return row => {
    const cantidad = Number(row[accessor]);

    return (
      <div className={fieldClassName(row) + " text-right"}>
        {!Number.isNaN(cantidad) && row[accessor] != null
          ? formatMoney(cantidad, 2)
          : "-"}
      </div>
    );
  };
}

function fieldClassName(row: AnualidadSnapshot) {
  let className = s.field;

  if (!row.facturaValida) {
    className += ` ${s.facturaCancelada}`;
  }

  return className;
}
